import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type AchievementCategory = 'tasks' | 'streaks' | 'social' | 'collection' | 'special'

export interface Achievement {
  id: string
  title: string
  description: string
  icon: string
  category: AchievementCategory
  requirement: number
  gemReward: number
  xpReward: number
  isSecret?: boolean
}

export const ACHIEVEMENTS: Achievement[] = [
  // Task achievements
  { id: 'first_quest', title: 'First Step', description: 'Complete your first quest', icon: '⚔️', category: 'tasks', requirement: 1, gemReward: 10, xpReward: 50 },
  { id: 'quest_10', title: 'Adventurer', description: 'Complete 10 quests', icon: '🗡️', category: 'tasks', requirement: 10, gemReward: 25, xpReward: 100 },
  { id: 'quest_50', title: 'Veteran', description: 'Complete 50 quests', icon: '⚔️', category: 'tasks', requirement: 50, gemReward: 50, xpReward: 250 },
  { id: 'quest_100', title: 'Quest Master', description: 'Complete 100 quests', icon: '🏆', category: 'tasks', requirement: 100, gemReward: 100, xpReward: 500 },
  { id: 'quest_500', title: 'Legendary Hero', description: 'Complete 500 quests', icon: '👑', category: 'tasks', requirement: 500, gemReward: 250, xpReward: 1000 },
  { id: 'boss_1', title: 'Boss Slayer', description: 'Defeat your first boss', icon: '👹', category: 'tasks', requirement: 1, gemReward: 25, xpReward: 100 },
  { id: 'boss_10', title: 'Boss Hunter', description: 'Defeat 10 bosses', icon: '💀', category: 'tasks', requirement: 10, gemReward: 75, xpReward: 300 },

  // Streak achievements
  { id: 'streak_3', title: 'Getting Started', description: 'Maintain a 3-day habit streak', icon: '🔥', category: 'streaks', requirement: 3, gemReward: 15, xpReward: 75 },
  { id: 'streak_7', title: 'One Week Strong', description: 'Maintain a 7-day habit streak', icon: '🔥', category: 'streaks', requirement: 7, gemReward: 30, xpReward: 150 },
  { id: 'streak_14', title: 'Two Week Warrior', description: 'Maintain a 14-day habit streak', icon: '🔥', category: 'streaks', requirement: 14, gemReward: 50, xpReward: 250 },
  { id: 'streak_30', title: 'Monthly Master', description: 'Maintain a 30-day habit streak', icon: '💪', category: 'streaks', requirement: 30, gemReward: 100, xpReward: 500 },
  { id: 'streak_100', title: 'Unstoppable', description: 'Maintain a 100-day habit streak', icon: '🌟', category: 'streaks', requirement: 100, gemReward: 300, xpReward: 1500 },
  { id: 'login_7', title: 'Dedicated', description: 'Login for 7 consecutive days', icon: '📅', category: 'streaks', requirement: 7, gemReward: 25, xpReward: 100 },
  { id: 'login_30', title: 'Committed', description: 'Login for 30 consecutive days', icon: '📅', category: 'streaks', requirement: 30, gemReward: 100, xpReward: 500 },

  // Social/Affection achievements
  { id: 'affection_100', title: 'New Friend', description: 'Reach 100 affection with any waifu', icon: '💕', category: 'social', requirement: 100, gemReward: 20, xpReward: 100 },
  { id: 'affection_500', title: 'Close Bond', description: 'Reach 500 affection with any waifu', icon: '💗', category: 'social', requirement: 500, gemReward: 50, xpReward: 250 },
  { id: 'affection_1000', title: 'True Love', description: 'Reach max affection with any waifu', icon: '❤️', category: 'social', requirement: 1000, gemReward: 150, xpReward: 750 },
  { id: 'all_waifus', title: 'Harem Protagonist', description: 'Reach 500+ affection with all waifus', icon: '👯', category: 'social', requirement: 3, gemReward: 200, xpReward: 1000 },

  // Collection achievements
  { id: 'gacha_1', title: 'Lucky Draw', description: 'Pull from the gacha for the first time', icon: '🎰', category: 'collection', requirement: 1, gemReward: 10, xpReward: 50 },
  { id: 'gacha_50', title: 'Gacha Addict', description: 'Pull 50 times from the gacha', icon: '🎰', category: 'collection', requirement: 50, gemReward: 50, xpReward: 250 },
  { id: 'outfit_5', title: 'Fashion Starter', description: 'Collect 5 outfits', icon: '👗', category: 'collection', requirement: 5, gemReward: 30, xpReward: 150 },
  { id: 'outfit_10', title: 'Fashionista', description: 'Collect 10 outfits', icon: '👗', category: 'collection', requirement: 10, gemReward: 75, xpReward: 375 },
  { id: 'ultra_rare', title: 'Jackpot!', description: 'Pull an Ultra Rare outfit', icon: '✨', category: 'collection', requirement: 1, gemReward: 50, xpReward: 250 },

  // Special/Level achievements
  { id: 'level_5', title: 'Rising Star', description: 'Reach level 5', icon: '⭐', category: 'special', requirement: 5, gemReward: 25, xpReward: 0 },
  { id: 'level_10', title: 'Experienced', description: 'Reach level 10', icon: '⭐', category: 'special', requirement: 10, gemReward: 50, xpReward: 0 },
  { id: 'level_25', title: 'Elite', description: 'Reach level 25', icon: '🌟', category: 'special', requirement: 25, gemReward: 100, xpReward: 0 },
  { id: 'level_50', title: 'Champion', description: 'Reach level 50', icon: '🏅', category: 'special', requirement: 50, gemReward: 200, xpReward: 0 },
  { id: 'pomodoro_10', title: 'Focused', description: 'Complete 10 pomodoro sessions', icon: '🍅', category: 'special', requirement: 10, gemReward: 30, xpReward: 150 },
  { id: 'pomodoro_50', title: 'Deep Work', description: 'Complete 50 pomodoro sessions', icon: '🍅', category: 'special', requirement: 50, gemReward: 75, xpReward: 375 },
]

interface AchievementsState {
  unlockedAchievements: string[]
  newlyUnlocked: string[]

  // Stats tracking
  stats: {
    tasksCompleted: number
    bossesDefeated: number
    bestHabitStreak: number
    loginStreak: number
    totalGachaPulls: number
    outfitsCollected: number
    ultraRaresPulled: number
    pomodorosCompleted: number
  }

  // Actions
  checkAndUnlockAchievement: (achievementId: string) => Achievement | null
  updateStat: (stat: keyof AchievementsState['stats'], value: number) => Achievement[]
  incrementStat: (stat: keyof AchievementsState['stats']) => Achievement[]
  checkLevelAchievements: (level: number) => Achievement[]
  checkAffectionAchievements: (affection: number) => Achievement[]
  isUnlocked: (achievementId: string) => boolean
  clearNewlyUnlocked: () => void
  getProgress: (achievementId: string) => number
}

export const useAchievementsStore = create<AchievementsState>()(
  persist(
    (set, get) => ({
      unlockedAchievements: [],
      newlyUnlocked: [],
      stats: {
        tasksCompleted: 0,
        bossesDefeated: 0,
        bestHabitStreak: 0,
        loginStreak: 0,
        totalGachaPulls: 0,
        outfitsCollected: 0,
        ultraRaresPulled: 0,
        pomodorosCompleted: 0,
      },

      checkAndUnlockAchievement: (achievementId) => {
        const { unlockedAchievements } = get()
        if (unlockedAchievements.includes(achievementId)) return null

        const achievement = ACHIEVEMENTS.find((a) => a.id === achievementId)
        if (!achievement) return null

        set({
          unlockedAchievements: [...unlockedAchievements, achievementId],
          newlyUnlocked: [...get().newlyUnlocked, achievementId],
        })

        return achievement
      },

      updateStat: (stat, value) => {
        const newlyUnlockedList: Achievement[] = []

        set((state) => ({
          stats: { ...state.stats, [stat]: value },
        }))

        // Check achievements based on stat
        const checkAchievements = (achievementIds: string[]) => {
          achievementIds.forEach((id) => {
            const achievement = ACHIEVEMENTS.find((a) => a.id === id)
            if (achievement && value >= achievement.requirement && !get().unlockedAchievements.includes(id)) {
              const unlocked = get().checkAndUnlockAchievement(id)
              if (unlocked) newlyUnlockedList.push(unlocked)
            }
          })
        }

        // Map stats to achievements
        switch (stat) {
          case 'tasksCompleted':
            checkAchievements(['first_quest', 'quest_10', 'quest_50', 'quest_100', 'quest_500'])
            break
          case 'bossesDefeated':
            checkAchievements(['boss_1', 'boss_10'])
            break
          case 'bestHabitStreak':
            checkAchievements(['streak_3', 'streak_7', 'streak_14', 'streak_30', 'streak_100'])
            break
          case 'loginStreak':
            checkAchievements(['login_7', 'login_30'])
            break
          case 'totalGachaPulls':
            checkAchievements(['gacha_1', 'gacha_50'])
            break
          case 'outfitsCollected':
            checkAchievements(['outfit_5', 'outfit_10'])
            break
          case 'ultraRaresPulled':
            checkAchievements(['ultra_rare'])
            break
          case 'pomodorosCompleted':
            checkAchievements(['pomodoro_10', 'pomodoro_50'])
            break
        }

        return newlyUnlockedList
      },

      incrementStat: (stat) => {
        const currentValue = get().stats[stat]
        return get().updateStat(stat, currentValue + 1)
      },

      checkLevelAchievements: (level) => {
        const levelAchievements = ['level_5', 'level_10', 'level_25', 'level_50']
        const newlyUnlockedList: Achievement[] = []

        levelAchievements.forEach((id) => {
          const achievement = ACHIEVEMENTS.find((a) => a.id === id)
          if (achievement && level >= achievement.requirement && !get().unlockedAchievements.includes(id)) {
            const unlocked = get().checkAndUnlockAchievement(id)
            if (unlocked) newlyUnlockedList.push(unlocked)
          }
        })

        return newlyUnlockedList
      },

      checkAffectionAchievements: (affection) => {
        const affectionAchievements = ['affection_100', 'affection_500', 'affection_1000']
        const newlyUnlockedList: Achievement[] = []

        affectionAchievements.forEach((id) => {
          const achievement = ACHIEVEMENTS.find((a) => a.id === id)
          if (achievement && affection >= achievement.requirement && !get().unlockedAchievements.includes(id)) {
            const unlocked = get().checkAndUnlockAchievement(id)
            if (unlocked) newlyUnlockedList.push(unlocked)
          }
        })

        return newlyUnlockedList
      },

      isUnlocked: (achievementId) => get().unlockedAchievements.includes(achievementId),

      clearNewlyUnlocked: () => set({ newlyUnlocked: [] }),

      getProgress: (achievementId) => {
        const achievement = ACHIEVEMENTS.find((a) => a.id === achievementId)
        if (!achievement) return 0

        const { stats } = get()

        // Map achievement to stat
        const statMap: Record<string, keyof typeof stats> = {
          first_quest: 'tasksCompleted',
          quest_10: 'tasksCompleted',
          quest_50: 'tasksCompleted',
          quest_100: 'tasksCompleted',
          quest_500: 'tasksCompleted',
          boss_1: 'bossesDefeated',
          boss_10: 'bossesDefeated',
          streak_3: 'bestHabitStreak',
          streak_7: 'bestHabitStreak',
          streak_14: 'bestHabitStreak',
          streak_30: 'bestHabitStreak',
          streak_100: 'bestHabitStreak',
          login_7: 'loginStreak',
          login_30: 'loginStreak',
          gacha_1: 'totalGachaPulls',
          gacha_50: 'totalGachaPulls',
          outfit_5: 'outfitsCollected',
          outfit_10: 'outfitsCollected',
          ultra_rare: 'ultraRaresPulled',
          pomodoro_10: 'pomodorosCompleted',
          pomodoro_50: 'pomodorosCompleted',
        }

        const stat = statMap[achievementId]
        if (!stat) return 0

        return Math.min(stats[stat] / achievement.requirement, 1)
      },
    }),
    {
      name: 'waifudo-achievements',
    }
  )
)
